/**
 * 创建页右侧常驻工作台(cockpit)— 2026-06-04
 *
 * 治"创建页割裂":把「实时预览 + 关键设置摘要 + 积分明细 + 生成按钮」收进一个常驻右栏,
 * 左栏专注输入。预览按**真实比例**画框,所见即所得(竖版/方形一眼可辨)。
 */
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { ratioToCss } from "@/shared/lib/video/aspect";
import { FONT_LABEL, VOICE_TONE_LABEL, ratioLabel } from "@/shared/lib/video/labels";
import {
  videoStyleByTone,
  videoStyleLabelForTone,
  videoStylePlaceholderGradient,
  videoStyleStaticUrl,
} from "../../ui/components/videoStyleOptions";
import type { FormState } from "../../model/videoCreateTypes";
import { isAutoMotion, pickedMotion } from "../../model/motionPick";
import type { PanelShotData } from "../../model/panelShot";

interface Props {
  form: FormState;
  shots: PanelShotData[];
  /** 模版封面(模版模式)兜底预览图 */
  templateCoverUrl?: string;
  effectiveDurationSec: number;
  /** 2026-08-04 导演自动配动作:编排回执快照(「动作×N(AI 配)」概览标注判据)。 */
  autoMotion?: Record<number, string>;
}

/** 预览图优先级:已出的首帧 → 模版封面 → 风格样例 → 渐变占位。 */
function previewSrc(
  form: FormState,
  shots: PanelShotData[],
  templateCoverUrl?: string
): string | undefined {
  const frame = shots.find((s) => s.firstFrameUrl)?.firstFrameUrl;
  if (frame) return frame;
  if (templateCoverUrl) return templateCoverUrl;
  const opt = videoStyleByTone(form.briefStyle ?? null);
  return opt ? videoStyleStaticUrl(opt) : undefined;
}

export function VideoCreateCockpit({
  form,
  shots,
  templateCoverUrl,
  effectiveDurationSec,
  autoMotion,
}: Props) {
  const src = previewSrc(form, shots, templateCoverUrl);
  const styleLabel = videoStyleLabelForTone(form.briefStyle ?? null) ?? "自由发挥";
  const framed = shots.filter((s) => s.firstFrameUrl).length;
  // 概览「动作×N(AI 配)」:读的是 shots 真实 references(不比实际乐观);
  // AI 配的条数按 isAutoMotion 判(用户换/清后自动降格)。
  const motionShots = shots.filter((s) => pickedMotion(s)).length;
  const autoMotionShots = autoMotion
    ? shots.filter((s, i) => isAutoMotion(autoMotion, i, s)).length
    : 0;
  const motionChip =
    motionShots > 0
      ? `动作×${motionShots}${
          autoMotionShots > 0
            ? `(AI 配${autoMotionShots < motionShots ? ` ${autoMotionShots}` : ""})`
            : ""
        }`
      : null;

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-lg border bg-card">
        {/* 真实比例预览框 */}
        <div className="flex justify-center bg-black/90">
          <div
            className="relative flex max-h-[44vh] w-full items-center justify-center overflow-hidden"
            style={{
              aspectRatio: ratioToCss(form.ratio, "9 / 16"),
              background: src
                ? undefined
                : videoStylePlaceholderGradient(form.briefStyle ?? "free"),
            }}
          >
            {src ? (
              <>
                <img
                  src={src}
                  alt=""
                  aria-hidden
                  className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-xl"
                />
                <img src={src} alt="预览" className="relative h-full w-full object-contain" />
              </>
            ) : (
              <span className="px-3 text-center text-xs text-white/90">
                {ratioLabel(form.ratio)} 预览
                <br />
                选风格 / AI 编排后显示
              </span>
            )}
            <span className="absolute left-2 top-2 rounded bg-black/55 px-1.5 py-0.5 font-mono text-2xs text-white backdrop-blur-sm">
              {ratioLabel(form.ratio)}
            </span>
          </div>
        </div>

        {/* 关键设置摘要 */}
        <div className="space-y-2 p-3 text-xs">
          <div className="flex flex-wrap gap-1.5">
            <Chip>{styleLabel}</Chip>
            {form.briefPurpose && <Chip>{form.briefPurpose}</Chip>}
            <Chip>{effectiveDurationSec}s</Chip>
            {shots.length >= 2 && <Chip>{shots.length} 镜</Chip>}
            {motionChip && <Chip>{motionChip}</Chip>}
          </div>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-muted-foreground">
            <Row k="比例" v={ratioLabel(form.ratio)} />
            <Row k="清晰度" v={form.resolution} />
            {form.voiceTone && <Row k="配音" v={VOICE_TONE_LABEL[form.voiceTone] || "默认音色"} />}
            {form.fontStyle && <Row k="字幕" v={FONT_LABEL[form.fontStyle] || "默认字体"} />}
            {shots.length >= 1 && <Row k="首帧" v={`${framed}/${shots.length}`} />}
          </dl>
        </div>
      </div>
    </div>
  );
}

/**
 * 出片条(积分 + 生成)—— 2026-08-06 从 cockpit 里拆出来**单独常驻**。
 *
 * ★为什么必须拆:幕二右栏现在是双态的检视面板(没选镜 = 全片视图、选中 = 这一镜)。
 * 积分+生成原本长在「全片视图」那一半里,于是「我正在看第 3 镜」时出片按钮**整个消失** ——
 * 用户得先想明白「要先点回全片」才出得了片。这和幕一犯的是同一个病:
 * 版面结构决定了操作可不可达,而不是用户的意图决定。
 *
 * 出片是这一页的终点,任何时候都得在。所以它从检视面板里搬出来,挂在右栏底部恒显。
 */
export function VideoSubmitBar({
  credits,
  onSubmit,
  submitting,
  busy,
  busyLabel,
}: {
  /**
   * **本次点「生成视频」会扣的钱 = 只有成片费**(video.render × 秒数)。
   *
   * ★0926 修(演示机实测):此前传的是「台本图 + 成片」—— 而台本图在**编排时**已逐张扣过
   *   (api `generateFrames`,action_type video_frame),点生成视频只扣成片:r1 报 1491 扣 1425、
   *   r2 报 3048 扣 2850。这条出片条只在幕二(已编排)出现,所以台本图在这里一律是「已扣」。
   *   不在这里印「已扣 N 张 = X」:失败镜不扣、重出镜再扣,前端的张数只是预估,印成已扣金额就是编数。
   */
  credits: number;
  onSubmit: () => void;
  submitting?: boolean;
  busy?: boolean;
  busyLabel?: string;
}) {
  return (
    <div className="space-y-2 rounded-lg border bg-card p-3">
      <div className="text-xs text-muted-foreground">
        {credits > 0 ? (
          <>
            本次预计扣 <span className="font-medium text-foreground">{credits}</span> 积分
            <div className="mt-0.5 opacity-70" data-testid="video-submit-frame-note">
              只含成片费;台本图(如有)编排时已按张扣过,不再重复计
            </div>
          </>
        ) : (
          "积分按实际出片时长与分辨率结算"
        )}
      </div>
      <Button className="w-full" onClick={onSubmit} disabled={submitting || busy}>
        {submitting ? (
          <>
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            提交中…
          </>
        ) : busy ? (
          (busyLabel ?? "处理中…")
        ) : /* ★ 这颗按钮上一版是裸 `~${credits} 积分`,而 credits 在拿不到费率时是 0
               ⇒ 屏幕上是「生成视频(~0 积分)」,**用户当成免费**。
               上面那段说明文字有 `credits > 0` 的守卫,按钮没有 —— 而按钮是最显眼的那处。 */
        credits > 0 ? (
          `生成视频(~${credits} 积分)`
        ) : (
          "生成视频"
        )}
      </Button>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-2xs text-foreground">
      {children}
    </span>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt className="text-foreground/70">{k}</dt>
      <dd className="text-right font-mono text-foreground">{v}</dd>
    </>
  );
}
